import type { Agency, AgencyCategory } from '@saaga/shared/interfaces/agencies.interface'
import { FileTextIcon, InfoIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import { agencyCategoryLabelMap } from './agencies.mock'
import { formatUsageCount } from './agency.util'
import { PriceRangeIndicator } from './price-range-indicator.atom'
import { StarRating } from './star-rating.atom'

interface AgencyCardStatsProps {
  rating: number
  reviewCount: number
  usageCount: number
  priceRange: Agency['priceRange']
  categoryList: AgencyCategory[]
}

export const AgencyCardStats = ({
  rating,
  reviewCount,
  usageCount,
  priceRange,
  categoryList,
}: AgencyCardStatsProps): ReactNode => (
  <div className="agency-card-stats mt-auto pb-3 flex items-center gap-3 flex-wrap">
    <div className="flex items-center gap-1.5">
      <StarRating rating={rating} size="sm" />
      {reviewCount > 0 && <span className="text-xs text-muted-foreground">({reviewCount})</span>}
    </div>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 text-xs text-muted-foreground cursor-help">
          <FileTextIcon className="size-3" />
          <span>{formatUsageCount(usageCount)}</span>
          <InfoIcon className="size-3 text-muted-foreground/70" />
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        Total number of reports generated across all users and use cases using this agency.
      </TooltipContent>
    </Tooltip>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <PriceRangeIndicator level={priceRange} size="sm" />
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        Dynamic pricing indicator based on overall marketplace costs.
      </TooltipContent>
    </Tooltip>
    <div className="flex gap-2">
      {categoryList.length > 0 &&
        categoryList.map((category) => (
          <Badge key={category} variant="outline" className="text-xs font-normal">
            {agencyCategoryLabelMap[category]}
          </Badge>
        ))}
    </div>
  </div>
)

AgencyCardStats.displayName = 'AgencyCardStats'
